<template>
  <div class="sales-trend">
    <div class="sales-trend__head">
      <div class="sales-trend__title">{{ $t('salesTrend') }}</div>
      <div class="sales-trend__legend">
        <span class="sales-trend__dot" />
        {{ $t('sell') }}
      </div>
    </div>
    <div class="sales-trend__body">
      <apexchart
        v-if="hasData"
        :key="chartKey"
        type="line"
        height="240"
        :options="chartOptions"
        :series="chartSeries"
      />
      <div
        v-else-if="loading"
        class="sales-trend__empty"
      >
        <v-progress-circular
          :width="3"
          color="blue"
          indeterminate
          size="22"
        />
      </div>
      <div
        v-else
        class="sales-trend__empty"
      >
        <home-empty-state />
      </div>
    </div>
  </div>
</template>

<script>
import HomeEmptyState from './HomeEmptyState.vue'

export default {
  name: 'HomeSalesTrend',
  components: { HomeEmptyState },
  props: {
    items: {
      type: Array,
      default: () => []
    },
    dateType: {
      type: String,
      default: '7days'
    }
  },
  data () {
    return {
      loading: false,
      localItems: []
    }
  },
  computed: {
    rows () {
      const source = this.items && this.items.length ? this.items : this.localItems
      return this.toRows(source)
    },
    hasData () {
      return this.rows.some(item => (parseFloat(item.sell) || 0) > 0)
    },
    chartKey () {
      return this.dateType + '-' + this.rows.length
    },
    chartSeries () {
      return [{
        name: this.$t('sell'),
        data: this.rows.map(item => item.sell)
      }]
    },
    chartOptions () {
      const self = this
      return {
        chart: {
          type: 'line',
          toolbar: { show: false },
          fontFamily: 'IRANSans',
          zoom: { enabled: false },
          animations: { enabled: false },
          parentHeightOffset: 0,
          offsetX: this.$vuetify.rtl ? 10 : 0
        },
        colors: ['#0066FF'],
        dataLabels: { enabled: false },
        stroke: {
          curve: 'smooth',
          width: 2
        },
        markers: {
          size: this.rows.length > 12 ? 0 : 4,
          colors: ['#fff'],
          strokeColors: '#0066FF',
          strokeWidth: 2
        },
        grid: {
          borderColor: '#E5E7EB',
          strokeDashArray: 4,
          padding: {
            top: 8,
            bottom: 0,
            left: 4,
            right: this.$vuetify.rtl ? -12 : 4
          },
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } }
        },
        legend: { show: false },
        tooltip: {
          y: {
            formatter (value) {
              return self.$numberFormat(value) + ' ' + (self.$store.state.currency.name || '')
            }
          }
        },
        xaxis: {
          categories: this.rows.map(item => item.key),
          tickAmount: this.rows.length > 12 ? 7 : undefined,
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: {
            rotate: 0,
            hideOverlappingLabels: true,
            style: { fontSize: '10px', colors: '#6B7280' }
          }
        },
        yaxis: {
          opposite: !!this.$vuetify.rtl,
          min: 0,
          labels: {
            minWidth: 0,
            maxWidth: 84,
            padding: 0,
            offsetX: this.$vuetify.rtl ? 6 : 0,
            style: { fontSize: '10px', colors: '#6B7280' },
            formatter (value) {
              return self.$numberFormat(value)
            }
          }
        }
      }
    }
  },
  watch: {
    dateType () {
      if (!this.items || !this.items.length) this.fetchList()
    }
  },
  mounted () {
    if (!this.items || !this.items.length) this.fetchList()
  },
  methods: {
    toRows (list) {
      if (!list) return []
      let arr = list
      if (!Array.isArray(arr)) {
        if (arr.data && Array.isArray(arr.data)) arr = arr.data
        else if (arr.items && Array.isArray(arr.items)) arr = arr.items
        else arr = Object.values(arr)
      }
      if (!Array.isArray(arr)) return []
      return arr.map(item => {
        if (item == null || typeof item !== 'object') {
          return { key: String(item), sell: 0 }
        }
        const key = item.key != null ? item.key : (item.date != null ? item.date : item.label)
        const sell = parseFloat(item.sell != null ? item.sell : (item.value != null ? item.value : item.amount))
        return {
          key: key == null ? '' : String(key),
          sell: Number.isNaN(sell) ? 0 : sell
        }
      }).filter(item => item.key !== '')
    },
    fetchList () {
      this.loading = true
      this.$axios.get(this.$endpoint + 'widgets', {
        params: {
          type: 'getSellChart',
          dateType: this.dateType
        }
      }).then(r => {
        this.localItems = this.toRows(r.data)
      }).catch(() => {
        this.localItems = []
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style scoped>
.sales-trend {
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 4px;
  padding: 12px 14px 8px;
  min-height: 292px;
}
.sales-trend__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}
.sales-trend__title {
  font-size: 13px;
  font-weight: 700;
  color: #111;
}
.sales-trend__legend {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #6B7280;
}
.sales-trend__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #0066FF;
}
.sales-trend__body {
  min-height: 240px;
}
.sales-trend__empty {
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
